import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, ClipboardList, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { formatDate } from '../../lib/utils';

const CLASS_NAMES: Record<string, string> = {
  '1': 'Class 6', '2': 'Class 7', '3': 'Class 8',
  '4': 'Class 9', '5': 'Class 10', '6': 'Class 11', '7': 'Class 12',
};

export default function ExamMarksPage() {
  const { module, classId, examId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [marks, setMarks] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [clearConfirm, setClearConfirm] = useState<{ studentId: number; resultId: number; name: string } | null>(null);
  const displayClass = CLASS_NAMES[classId || ''] || `Class ${classId}`;

  const { data: exam, isLoading: examLoading } = useQuery({
    queryKey: ['exam', examId],
    queryFn: async () => (await api.get<any>(`/api/exams/${examId}`)).data,
    enabled: !!examId,
  });

  // Fetch students for this class - enabled as soon as we have classId
  const { data: students, isLoading: studentsLoading } = useQuery({
    queryKey: ['exam-students', classId],
    queryFn: async () => {
      const params = new URLSearchParams({ pageSize: '500' });
      if (classId) params.set('classId', classId);
      params.set('status', 'active');
      const res = await api.get<any>(`/api/students?${params.toString()}`);
      return res.data?.items || res.data || [];
    },
    enabled: !!classId,
  });

  // Fetch existing results for this exam using the new endpoint
  const { data: results, isLoading: resultsLoading } = useQuery({
    queryKey: ['exam-results', examId],
    queryFn: async () => {
      const res = await api.get<any>(`/api/exams/${examId}/results`);
      return res.data || [];
    },
    enabled: !!examId,
  });

  const allResults = useMemo(() => {
    const map: Record<number, any> = {};
    (results || []).forEach((r: any) => {
      const sid = r.student_id || r.result_student_id;
      if (sid) map[sid] = r;
    });
    return map;
  }, [results]);

  const saveAll = async () => {
    setSaving(true);
    const records = Object.entries(marks).map(([studentId, m]) => ({
      examId: Number(examId),
      studentId: Number(studentId),
      obtainedMarks: Number(m),
    }));
    if (records.length === 0) { toast.error('No marks to save'); setSaving(false); return; }
    try {
      for (const record of records) {
        await api.post('/api/exams/results', record);
      }
      toast.success(`${records.length} results saved`);
      queryClient.invalidateQueries({ queryKey: ['exam-results', examId] });
      queryClient.invalidateQueries({ queryKey: ['module-exams', classId] });
      setMarks({});
    } catch (err: any) {
      toast.error(err.message || 'Failed to save');
    }
    setSaving(false);
  };

  const clearResult = async (studentId: number, resultId: number) => {
    try {
      await api.delete(`/api/exams/results/${resultId}`);
      toast.success('Result cleared');
      queryClient.invalidateQueries({ queryKey: ['exam-results', examId] });
      queryClient.invalidateQueries({ queryKey: ['module-exams', classId] });
    } catch (err: any) { toast.error(err.message || 'Failed'); }
  };

  const isLoading = examLoading || studentsLoading || resultsLoading;

  if (!exam && !examLoading) {
    return (
      <div className="text-center py-16">
        <p className="text-lg font-semibold text-light-text">Exam not found</p>
        <Button variant="outline" className="mt-3" onClick={() => navigate(`/modules/${module}/classes/${classId}`)}>Back to Class Hub</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/modules/${module}/classes/${classId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <p className="text-xs text-light-text-muted">Exam / {displayClass}</p>
          <h1 className="text-xl font-black text-light-text dark:text-dark-text flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-rose-600" />
            {exam?.name || 'Loading...'}
          </h1>
          {exam && (
            <p className="text-xs text-light-text-muted mt-0.5">
              Total: {exam.total_marks} marks{exam.exam_date ? ` · ${formatDate(exam.exam_date)}` : ''}
            </p>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map(i => <div key={i} className="h-12 rounded-xl bg-light-elevated dark:bg-dark-elevated animate-pulse" />)}
        </div>
      ) : (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Mark Sheet</CardTitle>
            <Button size="sm" onClick={saveAll} disabled={saving || Object.keys(marks).length === 0} className="gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save All ({Object.keys(marks).length})
            </Button>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-light-border dark:divide-dark-border">
              {!students || students.length === 0 ? (
                <div className="text-center py-8 text-light-text-muted text-sm">No active students in this class</div>
              ) : (
                students.map((student: any, index: number) => {
                  const existing = allResults[student.id];
                  const displayMarks = marks[student.id] ?? (existing?.obtained_marks ?? '');
                  return (
                    <div key={student.id} className="flex items-center gap-3 py-2.5">
                      <div className="w-8 h-8 rounded-lg bg-light-elevated dark:bg-dark-elevated flex items-center justify-center text-xs font-bold text-light-text-muted shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-light-text dark:text-dark-text truncate">{student.student_name}</p>
                        <p className="text-[10px] text-light-text-muted">Roll {student.roll_no || '-'}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Input
                          type="number"
                          className="w-20 h-8 text-xs text-center"
                          placeholder="Marks"
                          value={displayMarks}
                          onChange={(e) => setMarks({ ...marks, [student.id]: e.target.value })}
                        />
                        {existing?.result_id && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => setClearConfirm({ studentId: student.id, resultId: existing.result_id, name: student.student_name })}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      )}
      <AlertDialog open={!!clearConfirm} onOpenChange={() => setClearConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear Result</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {clearConfirm?.name}'s result? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-500 hover:bg-rose-600 text-white" onClick={() => {
              if (clearConfirm) clearResult(clearConfirm.studentId, clearConfirm.resultId);
              setClearConfirm(null);
            }}>Clear</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
